// src/enums.ts
var ROLES = ["owner", "staff"];
var ORDER_STATUSES = [
  "draft",
  "confirmed",
  "preparing",
  "ready",
  "out_for_delivery",
  "completed",
  "cancelled"
];
var ORDER_TYPES = ["pickup", "delivery"];
var PAYMENT_METHODS = ["cash", "card_on_pickup"];
var ORDER_SOURCES = ["ai", "manual", "promo"];
var MESSAGE_DIRECTIONS = ["inbound", "outbound"];
var MESSAGE_STATUSES = ["received", "queued", "sent", "delivered", "read", "failed"];
var AI_INTENTS = ["order", "inquiry", "complaint", "feedback", "other"];
var AI_FEATURES = ["classify", "extract_order", "suggest_replies", "auto_reply", "diet_tags", "summarize", "insights"];
var AI_OUTCOMES = ["pending", "accepted", "edited", "rejected", "error", "auto"];
var TEMPLATE_CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"];
var NOTIFY_STATUSES = ["confirmed", "preparing", "ready", "out_for_delivery", "completed", "cancelled"];
var ORDER_STATUS_LABEL = {
  draft: "Draft",
  confirmed: "Confirmed",
  preparing: "Preparing",
  ready: "Ready",
  out_for_delivery: "Out for delivery",
  completed: "Completed",
  cancelled: "Cancelled"
};

// src/order-transitions.ts
var TRANSITIONS = {
  draft: ["confirmed", "cancelled"],
  confirmed: ["preparing", "cancelled"],
  preparing: ["ready", "out_for_delivery", "cancelled"],
  ready: ["completed", "cancelled"],
  out_for_delivery: ["completed", "cancelled"],
  completed: [],
  cancelled: []
};
function nextStatuses(from, type) {
  return TRANSITIONS[from].filter((to) => {
    if (to === "ready") return type === "pickup";
    if (to === "out_for_delivery") return type === "delivery";
    return true;
  });
}
function canTransition(from, to, type) {
  return nextStatuses(from, type).includes(to);
}
function primaryNextStatus(from, type) {
  return nextStatuses(from, type).find((s) => s !== "cancelled") ?? null;
}
function isEditable(status) {
  return status === "draft" || status === "confirmed";
}
var ACTIVE_ORDER_STATUSES = ["draft", "confirmed", "preparing", "ready", "out_for_delivery"];

// src/money.ts
function formatMoney(cents, currency = "LKR", locale = "en-LK") {
  return new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: 2 }).format(cents / 100);
}
function toCents(amount) {
  return Math.round(amount * 100);
}
function lineTotal(line) {
  return (line.unitPriceCents + (line.optionsDeltaCents ?? 0)) * line.quantity;
}
function orderTotals(lines, deliveryFeeCents = 0) {
  const subtotalCents = lines.reduce((sum, l) => sum + lineTotal(l), 0);
  return { subtotalCents, deliveryFeeCents, totalCents: subtotalCents + deliveryFeeCents };
}

// src/realtime.ts
var RT = {
  messageNew: "message.new",
  messageStatus: "message.status",
  conversationUpdated: "conversation.updated",
  orderCreated: "order.created",
  orderUpdated: "order.updated",
  aiDraft: "ai.draft",
  aiSuggestions: "ai.suggestions",
  templateStatus: "template.status"
};

// src/schemas/auth.ts
import { z } from "zod";
var loginSchema = z.object({
  email: z.string().email().transform((e) => e.toLowerCase()),
  password: z.string().min(1),
  /** Stay signed in (persistent cookie). false = signed out when the browser closes, or after 12 idle hours. */
  remember: z.boolean().default(true)
});
var createUserSchema = z.object({
  email: z.string().email().transform((e) => e.toLowerCase()),
  name: z.string().min(1).max(100),
  password: z.string().min(8).max(200),
  role: z.enum(ROLES)
});
var updateUserSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  password: z.string().min(8).max(200).optional(),
  role: z.enum(ROLES).optional(),
  active: z.boolean().optional()
});

// src/schemas/menu.ts
import { z as z2 } from "zod";

// src/diet.ts
var DIET_TAGS = ["vegan", "vegetarian", "egg", "dairy", "chicken", "beef", "pork", "mutton", "fish", "seafood"];
var MEAT_TAGS = ["chicken", "beef", "pork", "mutton", "fish", "seafood"];
var DIET_RESTRICTIONS = ["vegetarian", "vegan", "no_beef", "no_pork", "no_seafood"];
var DIET_RESTRICTION_LABELS = {
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  no_beef: "No beef",
  no_pork: "No pork",
  no_seafood: "No fish/seafood"
};
var isMeat = (tags) => tags.some((t) => MEAT_TAGS.includes(t));
function dietConflict(tags, restrictions) {
  for (const r of restrictions) {
    if (r === "vegan" && !tags.includes("vegan")) return r;
    if (r === "vegetarian" && (!tags.length || isMeat(tags))) return r;
    if (r === "no_beef" && tags.includes("beef")) return r;
    if (r === "no_pork" && tags.includes("pork")) return r;
    if (r === "no_seafood" && (tags.includes("fish") || tags.includes("seafood"))) return r;
  }
  return null;
}
var DIET_INFERENCE_MIN_LINES = 3;
function inferDiet(lines) {
  const tagged = lines.filter((t) => t.length);
  if (tagged.length < DIET_INFERENCE_MIN_LINES) return [];
  const meat = tagged.filter(isMeat);
  if (!meat.length) return tagged.every((t) => t.includes("vegan")) ? ["vegan", "vegetarian"] : ["vegetarian"];
  const out = [];
  if (meat.length >= DIET_INFERENCE_MIN_LINES) {
    if (!meat.some((t) => t.includes("beef"))) out.push("no_beef");
    if (!meat.some((t) => t.includes("pork"))) out.push("no_pork");
  }
  return out;
}
function normalizeDietTags(tags) {
  const set = new Set(tags);
  if (set.has("vegan")) set.add("vegetarian");
  return DIET_TAGS.filter((t) => set.has(t));
}
function dishKey(name) {
  return name.replace(/\s*\((full|half|large|small|regular)\)\s*$/i, "").trim().toLowerCase();
}

// src/schemas/menu.ts
var menuCategorySchema = z2.object({
  name: z2.string().min(1).max(80),
  sortOrder: z2.number().int().optional(),
  active: z2.boolean().optional()
});
var menuOptionSchema = z2.object({
  id: z2.string().uuid().optional(),
  name: z2.string().min(1).max(80),
  priceDeltaCents: z2.number().int().min(0).default(0)
});
var menuItemSchema = z2.object({
  categoryId: z2.string().uuid(),
  name: z2.string().min(1).max(120),
  description: z2.string().max(500).nullish(),
  priceCents: z2.number().int().min(0),
  imageUrl: z2.string().url().nullish(),
  available: z2.boolean().default(true),
  aliases: z2.array(z2.string().min(1).max(60)).max(20).default([]),
  sortOrder: z2.number().int().optional(),
  options: z2.array(menuOptionSchema).max(30).default([]),
  /** Sent by the owner → saved as reviewed. Omitted → unchanged. */
  dietTags: z2.array(z2.enum(DIET_TAGS)).max(DIET_TAGS.length).optional()
});
var dietTagsSchema = z2.object({ dietTags: z2.array(z2.enum(DIET_TAGS)).max(DIET_TAGS.length) });
var reorderSchema = z2.object({ ids: z2.array(z2.string().uuid()).min(1) });

// src/schemas/settings.ts
import { z as z3 } from "zod";
var hhmm = z3.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "use HH:MM");
var WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
var dayHours = z3.object({ closed: z3.boolean().default(false), open: hhmm.default("10:00"), close: hhmm.default("22:00") }).refine((d) => d.closed || d.open < d.close, { message: "close must be after open", path: ["close"] });
var defaultDay = { closed: false, open: "10:00", close: "22:00" };
var statusMessages = z3.object({
  confirmed: z3.string().max(1e3).default("Hi {name}, your order {number} is confirmed. Total {total}. We will let you know when it is ready."),
  preparing: z3.string().max(1e3).default("Your order {number} is being prepared."),
  ready: z3.string().max(1e3).default("Your order {number} is ready for pickup!"),
  out_for_delivery: z3.string().max(1e3).default("Your order {number} is on the way!"),
  completed: z3.string().max(1e3).default("Thank you for ordering with us, {name}!"),
  cancelled: z3.string().max(1e3).default("Sorry {name}, your order {number} has been cancelled.")
});
var settingsSchema = z3.object({
  restaurantName: z3.string().min(1).max(100).default("My Restaurant"),
  currency: z3.string().length(3).default("LKR"),
  timezone: z3.string().default("Asia/Colombo"),
  hours: z3.object({
    mon: dayHours.default(defaultDay),
    tue: dayHours.default(defaultDay),
    wed: dayHours.default(defaultDay),
    thu: dayHours.default(defaultDay),
    fri: dayHours.default(defaultDay),
    sat: dayHours.default(defaultDay),
    sun: dayHours.default(defaultDay)
  }).default({}),
  pickupEnabled: z3.boolean().default(true),
  deliveryEnabled: z3.boolean().default(true),
  deliveryFeeCents: z3.number().int().min(0).default(25e3),
  minimumOrderCents: z3.number().int().min(0).default(0),
  deliveryAreaNotes: z3.string().max(1e3).default(""),
  prepTimeMinutes: z3.number().int().min(1).max(240).default(25),
  statusMessages: statusMessages.default({}),
  aiAutoSuggest: z3.boolean().default(true),
  aiAutoExtract: z3.boolean().default(true),
  /** Owner's notes for AI replies: tone, FAQs, policies. Goes into every reply prompt. */
  aiReplyGuidelines: z3.string().max(3e3).default(""),
  /** AI answers simple questions by itself when staff haven't replied within the delay. Off by default. */
  aiAutoReply: z3.boolean().default(false),
  aiAutoReplyDelaySeconds: z3.number().int().min(0).max(3600).default(120),
  aiAutoReplyOutsideHoursOnly: z3.boolean().default(false),
  /** The AI stays out of a chat for this long after a staff member typed in it (0 = no pause). Receipts don't count. */
  aiAutoReplyStaffPauseMinutes: z3.number().int().min(0).max(240).default(30),
  /** Sent (at most every 12h per chat) when the AI hands a message to staff while the restaurant is closed. {opens} {name}. Empty = never. */
  aiClosedNotice: z3.string().max(500).default("Thanks for your message! We're closed right now and open again {opens}. Our team will get back to you as soon as we open."),
  address: z3.string().max(300).default(""),
  phone: z3.string().max(40).default(""),
  /** Customer receipt (print + WhatsApp PDF): optional registration/VAT number and a closing line. */
  receiptRegistrationNo: z3.string().max(60).default(""),
  receiptFooter: z3.string().max(300).default("Thank you! Come again."),
  /** Promotions: Meta's per-number daily limit for new numbers is 250 unique customers. */
  promoDailyLimit: z3.number().int().min(1).max(1e5).default(250),
  promoSendsPerMinute: z3.number().int().min(1).max(600).default(30),
  /** Auto-reply when a customer opts in/out by keyword (sent inside the open 24h window). */
  optOutReply: z3.string().max(500).default("You will no longer receive promotions from us. Reply START to subscribe again."),
  optInReply: z3.string().max(500).default("Thanks! You will now get our daily specials. Reply STOP any time to unsubscribe.")
});
var DEFAULT_SETTINGS = settingsSchema.parse({});

// src/schemas/order.ts
import { z as z4 } from "zod";
var orderItemInputSchema = z4.object({
  menuItemId: z4.string().uuid(),
  quantity: z4.number().int().min(1).max(50),
  optionIds: z4.array(z4.string().uuid()).max(20).default([]),
  notes: z4.string().max(300).nullish()
});
var locationSchema = z4.object({
  latitude: z4.number().min(-90).max(90),
  longitude: z4.number().min(-180).max(180),
  name: z4.string().nullish(),
  address: z4.string().nullish()
});
var createOrderSchema = z4.object({
  conversationId: z4.string().uuid().optional(),
  customerId: z4.string().uuid().optional(),
  type: z4.enum(ORDER_TYPES),
  items: z4.array(orderItemInputSchema).min(1).max(100),
  address: z4.string().max(500).nullish(),
  location: locationSchema.nullish(),
  notes: z4.string().max(1e3).nullish(),
  paymentMethod: z4.enum(PAYMENT_METHODS).default("cash"),
  confirm: z4.boolean().default(false)
  // create directly as confirmed (manual orders)
});
var updateOrderSchema = createOrderSchema.omit({ conversationId: true, customerId: true, confirm: true }).partial();
var transitionOrderSchema = z4.object({
  to: z4.enum(ORDER_STATUSES),
  reason: z4.string().max(300).optional()
});
var listOrdersQuerySchema = z4.object({
  status: z4.string().optional(),
  // comma-separated
  date: z4.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  customerId: z4.string().uuid().optional(),
  conversationId: z4.string().uuid().optional(),
  limit: z4.coerce.number().int().min(1).max(200).default(100)
});

// src/schemas/inbox.ts
import { z as z5 } from "zod";
var listConversationsQuerySchema = z5.object({
  filter: z5.enum(["all", "unread", "mine", "open"]).default("all"),
  search: z5.string().max(100).optional(),
  limit: z5.coerce.number().int().min(1).max(100).default(50),
  before: z5.string().optional()
});
var sendReplySchema = z5.object({
  text: z5.string().min(1).max(4096),
  replyToMessageId: z5.string().uuid().optional(),
  aiRunId: z5.string().uuid().optional()
  // set when the text came from an AI suggestion
});
var sendTemplateSchema = z5.object({
  name: z5.string().min(1),
  language: z5.string().min(2),
  bodyParams: z5.array(z5.string().max(1e3)).max(20).default([]),
  headerParams: z5.array(z5.string().max(1e3)).max(5).default([])
});
var assignSchema = z5.object({ userId: z5.string().uuid().nullable() });
var updateCustomerSchema = z5.object({
  name: z5.string().max(100).nullish(),
  defaultAddress: z5.string().max(500).nullish(),
  notes: z5.string().max(2e3).nullish(),
  dietRestrictions: z5.array(z5.enum(DIET_RESTRICTIONS)).max(DIET_RESTRICTIONS.length).optional()
});

// src/schemas/templates.ts
import { z as z6 } from "zod";
var templateButtonSchema = z6.object({
  type: z6.enum(["QUICK_REPLY", "URL", "PHONE_NUMBER"]),
  text: z6.string().min(1).max(25),
  url: z6.string().url().optional(),
  phone_number: z6.string().optional()
});
var createTemplateSchema = z6.object({
  name: z6.string().regex(/^[a-z0-9_]{1,512}$/, "lowercase letters, digits and underscores only"),
  language: z6.string().min(2).default("en_US"),
  category: z6.enum(TEMPLATE_CATEGORIES),
  /** TEXT (headerText) or IMAGE (headerImageUrl: a sample JPEG/PNG we host; each send supplies its own picture). */
  headerFormat: z6.enum(["TEXT", "IMAGE"]).default("TEXT"),
  headerImageUrl: z6.string().url().optional(),
  headerText: z6.string().max(60).optional(),
  headerExample: z6.string().max(60).optional(),
  body: z6.string().min(1).max(1024),
  bodyExamples: z6.array(z6.string().max(200)).max(20).default([]),
  footer: z6.string().max(60).optional(),
  buttons: z6.array(templateButtonSchema).max(10).default([])
});
function countPlaceholders(text) {
  const nums = [...text.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
  return nums.length ? Math.max(...nums) : 0;
}
var TEMPLATE_VARIABLES = [
  "customer.name",
  "order.number",
  "order.total",
  "order.eta",
  "order.type",
  "restaurant.name",
  "restaurant.phone"
];
var notificationTemplateSchema = z6.object({
  status: z6.enum(NOTIFY_STATUSES),
  templateName: z6.string().min(1),
  language: z6.string().min(2),
  bodyVariables: z6.array(z6.enum(TEMPLATE_VARIABLES)).max(20).default([])
});

// src/schemas/analytics.ts
import { z as z7 } from "zod";
var dateRangeSchema = z7.object({
  from: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/)
});
var timeseriesQuerySchema = dateRangeSchema.extend({
  metric: z7.enum(["orders", "revenue", "aov", "messages", "sentiment", "response_time"]),
  granularity: z7.enum(["day", "week"]).default("day")
});

// src/schemas/promotions.ts
import { z as z8 } from "zod";
var ymd = z8.string().regex(/^\d{4}-\d{2}-\d{2}$/, "use YYYY-MM-DD");
var setSpecialsSchema = z8.object({
  items: z8.array(
    z8.object({
      menuItemId: z8.string().uuid(),
      /** Optional price for that day only. Null = normal menu price, just highlighted. */
      specialPriceCents: z8.number().int().min(0).nullish(),
      note: z8.string().max(200).nullish()
    })
  ).max(30)
});
var specialsDateSchema = ymd;
function normalizeWaId(input, defaultCountry = "94") {
  const digits = input.replace(/[^\d]/g, "");
  const withCc = digits.startsWith("00") ? digits.slice(2) : digits.startsWith("0") ? defaultCountry + digits.slice(1) : digits;
  return /^\d{8,15}$/.test(withCc) ? withCc : null;
}
var createCustomerSchema = z8.object({
  phone: z8.string().min(6).max(30),
  name: z8.string().min(1).max(100),
  defaultAddress: z8.string().max(500).nullish()
});
var MARKETING_STATUSES = ["opted_in", "opted_out", "invited", "unknown"];
var listCustomersQuerySchema = z8.object({
  search: z8.string().max(100).optional(),
  marketing: z8.enum(MARKETING_STATUSES).optional(),
  limit: z8.coerce.number().int().min(1).max(200).default(20),
  offset: z8.coerce.number().int().min(0).default(0)
});
var importCustomersSchema = z8.object({
  rows: z8.array(z8.object({ phone: z8.string().min(1).max(30), name: z8.string().trim().max(100).nullish() })).min(1).max(2e3),
  consentConfirmed: z8.literal(true)
});
var marketingConsentSchema = z8.object({ optIn: z8.boolean() });
var OPT_OUT_KEYWORDS = ["STOP", "UNSUBSCRIBE", "STOP PROMOTIONS", "NO MORE"];
var OPT_IN_KEYWORDS = ["START", "SUBSCRIBE"];
var INVITE_REPLY_KEYWORDS = ["YES", "Y", "YES PLEASE"];
var INVITE_DECLINE_KEYWORDS = ["NO", "NO THANKS", "N"];
var INVITE_REPLY_DAYS = 14;
function consentKeyword(text) {
  const t = (text ?? "").trim().toUpperCase().replace(/[.!]+$/, "");
  if (OPT_OUT_KEYWORDS.includes(t)) return "opt_out";
  if (OPT_IN_KEYWORDS.includes(t)) return "opt_in";
  if (INVITE_REPLY_KEYWORDS.includes(t)) return "invite_reply";
  if (INVITE_DECLINE_KEYWORDS.includes(t)) return "invite_decline";
  return null;
}
var CAMPAIGN_KINDS = ["promotion", "invite"];
var CAMPAIGN_STATUSES = ["draft", "scheduled", "sending", "sent", "cancelled"];
var audienceSchema = z8.object({
  orderedWithinDays: z8.number().int().min(1).max(730).nullish(),
  notOrderedWithinDays: z8.number().int().min(1).max(730).nullish(),
  minOrders: z8.number().int().min(1).max(1e3).nullish(),
  orderedItemIds: z8.array(z8.string().uuid()).max(50).default([]),
  orderedCategoryIds: z8.array(z8.string().uuid()).max(50).default([]),
  /** Skip anyone who got a promotion in the last N days (frequency cap). */
  excludePromotedWithinDays: z8.number().int().min(0).max(60).default(3),
  /** Explicit list (e.g. picked by hand or suggested). Still subject to consent. */
  customerIds: z8.array(z8.string().uuid()).max(5e3).default([])
});
var CAMPAIGN_TOKENS = ["{name}", "{specials}"];
var campaignInputSchema = z8.object({
  name: z8.string().min(1).max(100),
  kind: z8.enum(CAMPAIGN_KINDS).default("promotion"),
  templateName: z8.string().min(1),
  language: z8.string().min(2),
  bodyParams: z8.array(z8.string().min(1).max(1e3)).max(20).default([]),
  headerParams: z8.array(z8.string().min(1).max(1e3)).max(5).default([]),
  /** Picture for an IMAGE-header template (one of our uploads / menu photos, JPEG or PNG). */
  headerImageUrl: z8.string().url().nullish(),
  /** Index of the template's quick-reply button that starts an order when tapped ("Order now"). */
  orderButtonIndex: z8.number().int().min(0).max(9).nullish(),
  /** Dishes that button orders; empty = that day's specials. */
  orderItemIds: z8.array(z8.string().uuid()).max(10).default([]),
  audience: audienceSchema.default({})
});
var audiencePreviewSchema = z8.object({ kind: z8.enum(CAMPAIGN_KINDS).default("promotion"), audience: audienceSchema.default({}) });
var sendCampaignSchema = z8.object({ scheduledAt: z8.string().datetime().nullish() });

// src/receipt.ts
function receiptNumber(o) {
  return `${o.businessDate.replaceAll("-", "")}-${o.number}`;
}
function receiptFilename(o) {
  return `Receipt-${receiptNumber(o)}.pdf`;
}
function buildReceipt(o, s) {
  const money = (cents) => formatMoney(cents, s.currency);
  const issuedAt = new Intl.DateTimeFormat("en-GB", {
    timeZone: s.timezone,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(o.confirmedAt ?? o.createdAt));
  const totals = [{ label: "Subtotal", amount: money(o.subtotalCents) }];
  if (o.deliveryFeeCents) totals.push({ label: "Delivery", amount: money(o.deliveryFeeCents) });
  totals.push({ label: "Total", amount: money(o.totalCents), strong: true });
  return {
    restaurantName: s.restaurantName,
    headerLines: [s.address, s.phone ? `Tel: ${s.phone}` : "", s.receiptRegistrationNo ? `Reg. No: ${s.receiptRegistrationNo}` : ""].filter(Boolean),
    receiptNo: receiptNumber(o),
    orderNumber: `#${o.number}`,
    issuedAt,
    orderType: o.type === "delivery" ? "Delivery" : "Pickup",
    customerLines: [o.customer.name ?? "", `+${o.customer.waId}`, o.type === "delivery" ? o.address ?? "" : ""].filter(Boolean),
    items: o.items.map((i) => ({
      quantity: i.quantity,
      name: i.name,
      details: [
        ...i.options.length ? [i.options.map((x) => x.name).join(", ")] : [],
        ...i.quantity > 1 ? [`${i.quantity} \xD7 ${money(i.lineTotalCents / i.quantity)}`] : [],
        ...i.notes ? [i.notes] : []
      ],
      amount: money(i.lineTotalCents)
    })),
    totals,
    payment: o.paymentMethod === "cash" ? "Cash" : "Card at pickup",
    footer: s.receiptFooter
  };
}
export {
  ACTIVE_ORDER_STATUSES,
  AI_FEATURES,
  AI_INTENTS,
  AI_OUTCOMES,
  CAMPAIGN_KINDS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_TOKENS,
  DEFAULT_SETTINGS,
  DIET_INFERENCE_MIN_LINES,
  DIET_RESTRICTIONS,
  DIET_RESTRICTION_LABELS,
  DIET_TAGS,
  INVITE_DECLINE_KEYWORDS,
  INVITE_REPLY_DAYS,
  INVITE_REPLY_KEYWORDS,
  MARKETING_STATUSES,
  MEAT_TAGS,
  MESSAGE_DIRECTIONS,
  MESSAGE_STATUSES,
  NOTIFY_STATUSES,
  OPT_IN_KEYWORDS,
  OPT_OUT_KEYWORDS,
  ORDER_SOURCES,
  ORDER_STATUSES,
  ORDER_STATUS_LABEL,
  ORDER_TYPES,
  PAYMENT_METHODS,
  ROLES,
  RT,
  TEMPLATE_CATEGORIES,
  TEMPLATE_VARIABLES,
  WEEKDAYS,
  assignSchema,
  audiencePreviewSchema,
  audienceSchema,
  buildReceipt,
  campaignInputSchema,
  canTransition,
  consentKeyword,
  countPlaceholders,
  createCustomerSchema,
  createOrderSchema,
  createTemplateSchema,
  createUserSchema,
  dateRangeSchema,
  dietConflict,
  dietTagsSchema,
  dishKey,
  formatMoney,
  importCustomersSchema,
  inferDiet,
  isEditable,
  lineTotal,
  listConversationsQuerySchema,
  listCustomersQuerySchema,
  listOrdersQuerySchema,
  locationSchema,
  loginSchema,
  marketingConsentSchema,
  menuCategorySchema,
  menuItemSchema,
  menuOptionSchema,
  nextStatuses,
  normalizeDietTags,
  normalizeWaId,
  notificationTemplateSchema,
  orderItemInputSchema,
  orderTotals,
  primaryNextStatus,
  receiptFilename,
  receiptNumber,
  reorderSchema,
  sendCampaignSchema,
  sendReplySchema,
  sendTemplateSchema,
  setSpecialsSchema,
  settingsSchema,
  specialsDateSchema,
  templateButtonSchema,
  timeseriesQuerySchema,
  toCents,
  transitionOrderSchema,
  updateCustomerSchema,
  updateOrderSchema,
  updateUserSchema
};
